import { PageHeader } from '@/components/patterns';
import { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/lib/hooks/use-toast';
import { useMonedaDeLaEmpresa } from '@/features/dynamic-pricing/hooks/use-moneda-de-la-empresa';
import { storeConnectionsApi } from '../services';
import { PushHistoryTable } from '../components/push-history-table';
import type { StorePushLog } from '../types';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 20;

export function PushHistoryPage() {
  const { toast } = useToast();
  // Revisión T11-T12 (punto 1): la moneda de la empresa para los montos del detalle.
  useMonedaDeLaEmpresa();
  const [logs, setLogs] = useState<StorePushLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: PAGE_SIZE };
      if (filterType !== 'ALL') params.pushType = filterType;
      if (filterStatus !== 'ALL') params.status = filterStatus;
      const result = await storeConnectionsApi.getHistory(params);
      setLogs(result.logs);
      setTotal(result.total);
    } catch {
      toast({ title: 'Error al cargar historial', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterStatus]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleRevert = async (logId: string) => {
    try {
      await storeConnectionsApi.revert(logId);
      toast({ title: 'Push revertido exitosamente' });
      fetchHistory();
    } catch (err: any) {
      toast({ title: 'Error al revertir', description: err?.message, variant: 'destructive' });
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        <PageHeader
          etiqueta="Integraciones"
          tonoEtiqueta="neutro"
          titulo="Historial de envíos"
          subtitulo="Cada envío de precios a una tienda conectada: qué se mandó, cuándo y si se aplicó."
        />

        {/* Filtros */}
        <div className="flex items-center gap-3">
          <Select
            value={filterType}
            onValueChange={(v) => {
              setFilterType(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los tipos</SelectItem>
              <SelectItem value="SMART_PRICING">Smart Pricing</SelectItem>
              <SelectItem value="RULE">Regla</SelectItem>
              <SelectItem value="DISCOUNT">Descuento</SelectItem>
              <SelectItem value="MANUAL">Manual</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filterStatus}
            onValueChange={(v) => {
              setFilterStatus(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los estados</SelectItem>
              <SelectItem value="APPLIED">Aplicado</SelectItem>
              <SelectItem value="REVERTED">Revertido</SelectItem>
              <SelectItem value="FAILED">Fallido</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabla */}
        <Card>
          <CardContent className="p-0">
            <PushHistoryTable logs={logs} onRevert={handleRevert} loading={loading} />
          </CardContent>
        </Card>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default PushHistoryPage;
